import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Portal } from './Portal.jsx';

const HOVER_DELAY = 400;
const GAP = 8;
const MARGIN = 8;

// The app's one tooltip: any element with a `data-tooltip` attribute shows its
// text in the system's graphite bubble (8/12 padding, radius 12, 12/600, max
// 240) — on hover after a short delay, at once on keyboard focus. It replaces
// the browser's `title` bubble, which ignores the design system and never
// shows for keyboard users. Mounted once by the app shell; the text is not an
// accessible name, so an icon-only control still needs its own aria-label.
export function TooltipLayer() {
  const [tip, setTip] = useState(null);
  const bubbleRef = useRef(null);
  const [style, setStyle] = useState({ visibility: 'hidden' });

  useEffect(() => {
    let timer = null;
    let current = null;

    function show(el, delay) {
      const text = el.getAttribute('data-tooltip');
      if (!text) return;
      // A tooltip that only repeats a label already fully on screen (a
      // sidebar item with its name showing) is noise; it still shows when
      // the label is hidden (the collapsed rail) or cut off with an ellipsis.
      const shown = el.innerText?.trim();
      if (shown === text && el.scrollWidth <= el.clientWidth) return;
      window.clearTimeout(timer);
      current = el;
      timer = window.setTimeout(() => {
        if (current !== el || !el.isConnected) return;
        setTip({ text, rect: el.getBoundingClientRect() });
      }, delay);
    }
    function hide() {
      window.clearTimeout(timer);
      current = null;
      setTip(null);
    }
    function onPointerOver(event) {
      if (event.pointerType === 'touch') return;
      const el = event.target.closest?.('[data-tooltip]');
      if (el === current) return;
      if (!el) {
        if (current) hide();
        return;
      }
      show(el, HOVER_DELAY);
    }
    function onFocusIn(event) {
      const el = event.target.closest?.('[data-tooltip]');
      if (!el) return;
      let keyboard = false;
      try {
        keyboard = event.target.matches(':focus-visible');
      } catch {
        keyboard = false;
      }
      if (keyboard) show(el, 0);
    }
    function onFocusOut(event) {
      if (current && !current.contains(event.relatedTarget)) hide();
    }
    function onKeyDown(event) {
      if (event.key === 'Escape' && current) hide();
    }

    document.addEventListener('pointerover', onPointerOver);
    document.addEventListener('focusin', onFocusIn);
    document.addEventListener('focusout', onFocusOut);
    document.addEventListener('pointerdown', hide, true);
    document.addEventListener('keydown', onKeyDown, true);
    window.addEventListener('scroll', hide, true);
    window.addEventListener('blur', hide);
    return () => {
      window.clearTimeout(timer);
      document.removeEventListener('pointerover', onPointerOver);
      document.removeEventListener('focusin', onFocusIn);
      document.removeEventListener('focusout', onFocusOut);
      document.removeEventListener('pointerdown', hide, true);
      document.removeEventListener('keydown', onKeyDown, true);
      window.removeEventListener('scroll', hide, true);
      window.removeEventListener('blur', hide);
    };
  }, []);

  // Above the element, centred; below when there is no room above; always
  // inside the viewport.
  useLayoutEffect(() => {
    if (!tip || !bubbleRef.current) {
      setStyle({ visibility: 'hidden' });
      return;
    }
    const b = bubbleRef.current.getBoundingClientRect();
    const { rect } = tip;
    let top = rect.top - GAP - b.height;
    if (top < MARGIN) top = rect.bottom + GAP;
    let left = rect.left + rect.width / 2 - b.width / 2;
    left = Math.max(
      MARGIN,
      Math.min(left, window.innerWidth - b.width - MARGIN),
    );
    setStyle({ top: Math.round(top), left: Math.round(left) });
  }, [tip]);

  if (!tip) return null;
  return (
    <Portal>
      <div ref={bubbleRef} className="app-tooltip" role="tooltip" style={style}>
        {tip.text}
      </div>
    </Portal>
  );
}
